import { ComponentProps, useEffect, useRef } from 'react';

import { styled } from '@mui/material';
import { useOverlayScrollbars } from 'overlayscrollbars-react';
import { Components } from 'react-virtuoso';

import { setRef } from '@cast/utils';

import { SCROLLER_THEME, scrollerStyles } from './constants';
import { SxProps, WithRef } from '../../types';

const Scroller = styled('div')(({ theme }) => scrollerStyles(theme));
type ScrollerProps = ComponentProps<NonNullable<Components['Scroller']>>;

export const VirtuosoScroller = ({
  ref,
  sx,
  style,
  children,
  ...rest
}: WithRef<HTMLDivElement, ScrollerProps> & {
  sx?: SxProps;
}) => {
  // Ref of timeout handle to 'unhide' vertical scroller after horizontal scrolling has ended
  const [initialize] = useOverlayScrollbars({
    options: {
      showNativeOverlaidScrollbars: false,
      overflow: {
        x: 'scroll',
        y: 'scroll',
      },
      scrollbars: {
        autoHide: 'move',
        theme: SCROLLER_THEME,
      },
    },
  });

  const rootRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    initialize({
      target: rootRef.current!,
      elements: {
        viewport: contentRef.current,
        content: contentRef.current,
      },
    });
  }, [initialize, ref, rootRef]);

  return (
    <Scroller
      ref={(el) => {
        if (el) {
          rootRef.current = el;
        }
      }}
      sx={sx}
      data-overlayscrollbars-initialize=""
    >
      <div
        data-overlayscrollbars-contents=""
        ref={(el) => {
          if (el) {
            contentRef.current = el;
            setRef(ref, el);
          }
        }}
        style={style}
        {...rest}
      >
        {children}
      </div>
    </Scroller>
  );
};
